import type { BreadcrumbItem } from "@tranquility-ai/theme-components/breadcrumbs";

import { caseFolderLabel, relabelCaseFolderSegments } from "./case-summary";

/** A breadcrumb the locker already built (path stays raw; label is display). */
export interface EvidenceNavCrumb {
  name: string;
  path: string;
}

/**
 * Visible crumbs from a locker nav stack. `startIndex` hides the case-root
 * prefix the header treats as home. Item ids are the raw paths.
 */
export function navStackToBreadcrumbItems(
  navStack: readonly EvidenceNavCrumb[],
  caseLabels: Map<string, string>,
  startIndex = 0,
): BreadcrumbItem[] {
  const start = Math.min(Math.max(0, startIndex), navStack.length);
  return navStack.slice(start).map((entry) => {
    const label = caseFolderLabel(entry.name, caseLabels);
    return { id: entry.path, label, title: label };
  });
}

/**
 * Crumbs from a slash-delimited path. Leading segments matching `omitPrefix`
 * are hidden. Each item id is the raw prefix path up to that segment, so
 * navigation does not use the relabeled text.
 */
export function pathToBreadcrumbItems(
  path: string,
  omitPrefix: string | null | undefined,
  caseLabels: Map<string, string>,
): { items: BreadcrumbItem[]; omitCount: number } {
  const full = path.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  const omitCount = countSegmentsToOmitDisplay(full, omitPrefix);
  const items = full.slice(omitCount).map((segment, displayIndex) => {
    const fullIndex = omitCount + displayIndex;
    const prefix = full.slice(0, fullIndex + 1).join("/");
    return {
      id: prefix,
      label: caseFolderLabel(segment, caseLabels),
      title: relabelCaseFolderSegments(prefix, caseLabels),
    };
  });
  return { items, omitCount };
}

const ARCHIVE_CONTAINER_SEGMENT =
  /\.(?:zip|pst|ost|ufdr|tar|gz|tgz|bz2|7z|rar|xz|mbox|mbx)$/i;

/**
 * Path through the first archive file when later segments live inside it
 * (`Manual/pack.zip/email/file.pdf` → `Manual/pack.zip`). A container with
 * nothing after it is not a member path.
 */
export function archiveContainerPrefix(path: string): string | null {
  const segments = path.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (segments[0]?.toLowerCase() === "files") return null;
  for (let i = 0; i < segments.length - 1; i++) {
    if (!ARCHIVE_CONTAINER_SEGMENT.test(segments[i] ?? "")) continue;
    return segments.slice(0, i + 1).join("/");
  }
  return null;
}

/** How many leading `segments` match `omitPrefix` (path or single segment). */
export function countSegmentsToOmitDisplay(
  segments: readonly string[],
  omitPrefix?: string | null,
): number {
  if (!omitPrefix?.trim() || segments.length === 0) return 0;
  const omitSegs = omitPrefix.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (omitSegs.length > 0) {
    let n = 0;
    for (let i = 0; i < omitSegs.length && i < segments.length; i++) {
      if (segments[i] !== omitSegs[i]) break;
      n++;
    }
    if (n > 0) return n;
  }
  const single = omitPrefix.trim();
  if (segments[0] === single || segments[0].toLowerCase() === single.toLowerCase()) {
    return 1;
  }
  return 0;
}
